import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchReport, handleError, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const InTransitMovementsReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, resetNavigation, state } = useNavigationHelperStore();
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    movement: null
  });
  
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    movement: null
  });

  useEffect(() => {
    fetchInTransitMovements(1);
  }, []);

  const fetchInTransitMovements = async (pageNumber) => {
    setIsLoading(true);
    try {
      const response = await fetchReport('IN_TRANSIT_MOVEMENTS', pageNumber);
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchInTransitMovements(value);
  };

  const handleMovementClick = (movement) => {
    navigate('/main/view-movement', { state: { id: movement.id } });
  };

  // Handle confirmation
  const handleConfirmClick = (movement, e) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      movement
    });
  };
  
  const handleConfirmClose = () => {
    setConfirmDialog({
      open: false,
      movement: null
    });
  };
  
  const handleConfirmMovement = async () => {
    const { movement } = confirmDialog;
    if (!movement) return;
    
    setIsLoading(true);
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}internal_movements?accept_movement&id=${movement.id}`,
        {},
        { withCredentials: true }
      );
      
      snackbar(t('reports.processedAccepted'));
      fetchInTransitMovements(page);
      handleConfirmClose();
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle rejection
  const handleRejectClick = (movement, e) => {
    e.stopPropagation();
    setRejectDialog({
      open: true,
      movement
    });
  };
  
  const handleRejectClose = () => {
    setRejectDialog({
      open: false,
      movement: null
    });
  };
  
  const handleRejectMovement = async () => {
    const { movement } = rejectDialog;
    if (!movement) return;
    
    setIsLoading(true);
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}movements/reject?id=${movement.id}`,
        {},
        { withCredentials: true }
      );
      
      snackbar(t('reports.movementRejected'));
      fetchInTransitMovements(page);
      handleRejectClose();
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const getTypeChip = (type) => {
    switch (type) {
      case 'IN':
        return <Chip label={t('reports.entryType')} color="success" size="small" />;
      case 'OUT':
        return <Chip label={t('reports.exitType')} color="error" size="small" />;
      case 'INTERNAL':
        return <Chip label={t('reports.internalType')} color="primary" size="small" />;
      default:
        return <Chip label={type || '-'} size="small" />;
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'PENDING':
        return <Chip label={t('reports.pendingStatus')} color="warning" size="small" />;
      case 'IN_TRANSIT':
        return <Chip label={t('reports.inTransitStatus')} color="info" size="small" />;
      case 'DRAFT':
        return <Chip label={t('reports.draftStatus')} variant="outlined" size="small" />;
      default:
        return <Chip label={status || '-'} size="small" />;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>      
      {data ? (
        <>
          <Box mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <Typography variant="h6">
                    {t('reports.inTransitMovements')} ({data.total_results || (data.content ? data.content.length : 0)})
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {data.content && data.content.length > 0 ? (
            <>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.date')}</TableCell>
                      <TableCell>{t('common.receipt')}</TableCell>
                      <TableCell align="center">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((movement) => (
                      <TableRow 
                        key={movement.id}
                        hover
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        <TableCell onClick={() => handleMovementClick(movement)}>
                          {formatDate2(movement.date)}
                        </TableCell>
                        <TableCell onClick={() => handleMovementClick(movement)}>
                          {movement.receipt || '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center">
                            <Button
                              startIcon={<CheckCircleIcon />}
                              size="small"
                              color="success"
                              onClick={(e) => handleConfirmClick(movement, e)}
                              sx={{ mr: 1 }}
                            >
                              {t('common.confirm')}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3} component={Paper} variant="outlined">
              {/* <CompareArrowsIcon color="action" sx={{ fontSize: 40, mb: 2, opacity: 0.6 }} /> */}
              <Typography variant="body1">{t('reports.noInTransitMovements')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('common.noData')}</Typography>
        </Box>
      )}
      
      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('view.confirmMovement')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('reports.confirmMovementDialog')}
            <br />
            {t('reports.confirmMovementDesc')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleConfirmMovement} 
            variant="contained" 
            color="success"
            autoFocus
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={handleRejectClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('view.rejectMovement')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('reports.rejectMovementDialog')}
            <br />
            {t('reports.irreversibleAction')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleRejectMovement} 
            variant="contained" 
            color="error"
            autoFocus
          >
            {t('reports.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InTransitMovementsReport;